/**
 * v4.37.0 — Mission Control Broadcast surface components.
 *
 * Three surfaces with identical content contract — only the chrome
 * differs. Each component:
 *   • Picks the user's language from broadcast.title / body / cta.label
 *     using i18n.language with EN fallback (the document holds raw
 *     pre-localized content; chrome lives in the locale files).
 *   • Reads `priority` to choose an accent (sky / amber / red).
 *   • Calls onDismiss(broadcast_id) to record the per-user dismissal.
 *
 * Used by:
 *   • AdminBroadcasts page → preview-before-publish (renders BroadcastPreview)
 *   • App layout           → StickyBroadcastBar + BroadcastModal mounts
 *   • Welcome.jsx          → inline announcement event (renders the same
 *                            visual via AnnouncementCard, kept there for
 *                            tight integration with the events panel)
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone, AlertTriangle, Sparkles, X, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';

const PRIORITY_TOKENS = {
    critical:  { bg: 'rgba(255, 75, 87, 0.10)',  border: 'rgba(255, 75, 87, 0.45)',  fg: '#FF4B57', Icon: AlertTriangle, eyebrowKey: 'critical' },
    important: { bg: 'rgba(255, 179, 71, 0.10)', border: 'rgba(255, 179, 71, 0.45)', fg: '#FFB347', Icon: Sparkles,      eyebrowKey: 'important' },
    normal:    { bg: 'rgba(74, 163, 255, 0.10)', border: 'rgba(74, 163, 255, 0.45)', fg: '#4AA3FF', Icon: Megaphone,     eyebrowKey: 'normal' },
};

// Pick the localized string from a {en, fr, es} object using the
// current i18n language, falling back to EN if missing/empty.
const pickLocale = (obj, lang) => {
    if (!obj) return '';
    const v = obj[lang];
    if (v && v.trim()) return v;
    return obj.en || '';
};

const goToCtaUrl = (url) => {
    if (!url) return;
    if (url.startsWith('/')) {
        window.location.assign(url);
    } else {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
};

// =====================================================================
// BroadcastPreview — used in the admin console; lays out all three
// surfaces vertically so the author sees what each variant becomes.
// =====================================================================
export const BroadcastPreview = ({ broadcast }) => {
    const { t } = useTranslation();
    return (
        <div className="space-y-6" data-testid="broadcast-preview-stack">
            <PreviewSection title={t('broadcasts.surface.inline')}>
                <InlineBroadcastCard broadcast={broadcast} onDismiss={null} />
            </PreviewSection>
            <PreviewSection title={t('broadcasts.surface.sticky')}>
                <StickyBroadcastInline broadcast={broadcast} onDismiss={null} />
            </PreviewSection>
            <PreviewSection title={t('broadcasts.surface.modal')}>
                <ModalBroadcastInline broadcast={broadcast} onDismiss={null} />
            </PreviewSection>
        </div>
    );
};

const PreviewSection = ({ title, children }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">{title}</div>
        {children}
    </div>
);

// =====================================================================
// InlineBroadcastCard — rendered in two places:
//   1. The admin preview pane
//   2. Mission Control's announcement slot (via Welcome.jsx)
// =====================================================================
export const InlineBroadcastCard = ({ broadcast, onDismiss }) => {
    const { t, i18n } = useTranslation();
    const tokens = PRIORITY_TOKENS[broadcast.priority] || PRIORITY_TOKENS.normal;
    const { Icon } = tokens;
    const title = pickLocale(broadcast.title, i18n.language);
    const body = pickLocale(broadcast.body, i18n.language);
    const ctaLabel = broadcast.cta ? pickLocale(broadcast.cta.label, i18n.language) : null;

    return (
        <div
            className="rounded-2xl p-5 border flex items-start gap-4 relative"
            style={{ background: tokens.bg, borderColor: tokens.border }}
            data-testid="broadcast-inline-card"
            data-broadcast-id={broadcast.id}
            data-priority={broadcast.priority}
        >
            <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${tokens.fg}22`, color: tokens.fg, border: `1px solid ${tokens.fg}55` }}
            >
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-1" style={{ color: tokens.fg }}>
                    {t(`broadcasts.priority_eyebrow.${tokens.eyebrowKey}`)} · {t('broadcasts.announcement_eyebrow')}
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{body}</p>
                {broadcast.cta && ctaLabel && (
                    <Button
                        size="sm"
                        className="mt-3"
                        onClick={() => goToCtaUrl(broadcast.cta.url)}
                        style={{ background: tokens.fg, color: '#000' }}
                        data-testid="broadcast-inline-cta"
                    >
                        {ctaLabel}<ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                )}
            </div>
            {onDismiss && (
                <button
                    onClick={() => onDismiss(broadcast.id)}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 -mt-1 -mr-1"
                    aria-label={t('broadcasts.dismiss')}
                    data-testid="broadcast-inline-dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
    );
};

// =====================================================================
// StickyBroadcastBar — top-of-app banner across authenticated routes.
// Mounted ONCE in the Layout shell.
// =====================================================================
const StickyBroadcastInline = ({ broadcast, onDismiss }) => {
    const { t, i18n } = useTranslation();
    const tokens = PRIORITY_TOKENS[broadcast.priority] || PRIORITY_TOKENS.normal;
    const { Icon } = tokens;
    const title = pickLocale(broadcast.title, i18n.language);
    const body = pickLocale(broadcast.body, i18n.language);
    const ctaLabel = broadcast.cta ? pickLocale(broadcast.cta.label, i18n.language) : null;

    return (
        <div
            className="w-full flex items-center gap-3 px-4 py-2.5 border-b"
            style={{ background: tokens.bg, borderColor: tokens.border }}
            data-testid="broadcast-sticky-bar"
            data-broadcast-id={broadcast.id}
            data-priority={broadcast.priority}
        >
            <Icon className="w-4 h-4 shrink-0" style={{ color: tokens.fg }} />
            <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
                <span className="text-sm font-semibold truncate">{title}</span>
                <span className="text-xs text-muted-foreground truncate">{body}</span>
            </div>
            {broadcast.cta && ctaLabel && (
                <button
                    onClick={() => goToCtaUrl(broadcast.cta.url)}
                    className="text-xs font-medium underline-offset-2 hover:underline shrink-0"
                    style={{ color: tokens.fg }}
                    data-testid="broadcast-sticky-cta"
                >
                    {ctaLabel}
                </button>
            )}
            {onDismiss && (
                <button
                    onClick={() => onDismiss(broadcast.id)}
                    className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    aria-label={t('broadcasts.dismiss')}
                    data-testid="broadcast-sticky-dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
    );
};

export const StickyBroadcastBar = ({ broadcast, onDismiss }) =>
    <StickyBroadcastInline broadcast={broadcast} onDismiss={onDismiss} />;

// =====================================================================
// ModalBroadcastInline — visual representation used in admin preview.
// The actual mount is a Dialog (see BroadcastModal below).
// =====================================================================
const ModalBroadcastInline = ({ broadcast }) => {
    const { i18n, t } = useTranslation();
    const tokens = PRIORITY_TOKENS[broadcast.priority] || PRIORITY_TOKENS.normal;
    const { Icon } = tokens;
    const title = pickLocale(broadcast.title, i18n.language);
    const body = pickLocale(broadcast.body, i18n.language);
    const ctaLabel = broadcast.cta ? pickLocale(broadcast.cta.label, i18n.language) : null;
    return (
        <div className="max-w-md mx-auto border rounded-xl p-6 bg-card shadow-lg" data-testid="broadcast-modal-preview">
            <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: `${tokens.fg}22`, color: tokens.fg, border: `1px solid ${tokens.fg}55` }}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-center mb-2" style={{ color: tokens.fg }}>
                {t(`broadcasts.priority_eyebrow.${tokens.eyebrowKey}`)}
            </div>
            <h3 className="text-xl font-semibold text-center mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground text-center whitespace-pre-wrap mb-4">{body}</p>
            {broadcast.cta && ctaLabel && (
                <div className="flex justify-center">
                    <Button size="sm" style={{ background: tokens.fg, color: '#000' }}>
                        {ctaLabel}<ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                </div>
            )}
        </div>
    );
};

export const BroadcastModal = ({ broadcast, onDismiss }) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(true);
    if (!broadcast) return null;
    const tokens = PRIORITY_TOKENS[broadcast.priority] || PRIORITY_TOKENS.normal;
    const { Icon } = tokens;
    const title = pickLocale(broadcast.title, i18n.language);
    const body = pickLocale(broadcast.body, i18n.language);
    const ctaLabel = broadcast.cta ? pickLocale(broadcast.cta.label, i18n.language) : null;

    const handleDismiss = () => {
        setOpen(false);
        if (onDismiss) onDismiss(broadcast.id);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
            <DialogContent
                className="sm:max-w-md"
                data-testid="broadcast-modal"
                data-broadcast-id={broadcast.id}
                data-priority={broadcast.priority}
            >
                {/* Accessibility: Radix Dialog requires a DialogTitle.
                  * We render the visual title as a styled <h3> below
                  * (better typography control); this hidden DialogTitle
                  * satisfies screen-reader announcement and silences
                  * the @radix-ui a11y warning. */}
                <DialogTitle className="sr-only">{title}</DialogTitle>
                <div className="flex justify-center mb-3">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center"
                        style={{ background: `${tokens.fg}22`, color: tokens.fg, border: `1px solid ${tokens.fg}55` }}>
                        <Icon className="w-6 h-6" />
                    </div>
                </div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-center mb-2" style={{ color: tokens.fg }}>
                    {t(`broadcasts.priority_eyebrow.${tokens.eyebrowKey}`)} · {t('broadcasts.announcement_eyebrow')}
                </div>
                <h3 className="text-xl font-semibold text-center mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground text-center whitespace-pre-wrap mb-4">{body}</p>
                <div className="flex justify-center gap-2">
                    <Button variant="outline" onClick={handleDismiss} data-testid="broadcast-modal-dismiss">
                        {t('broadcasts.dismiss')}
                    </Button>
                    {broadcast.cta && ctaLabel && (
                        <Button
                            onClick={() => { goToCtaUrl(broadcast.cta.url); handleDismiss(); }}
                            style={{ background: tokens.fg, color: '#000' }}
                            data-testid="broadcast-modal-cta"
                        >
                            {ctaLabel}<ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
